import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import './DeleteAccount.css';

type DeleteAccountResult = {
  success: boolean;
};

const deleteUserAccount = httpsCallable<undefined, DeleteAccountResult>(functions, 'deleteUserAccount');

export default function DeleteAccount() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    document.title = 'Delete Account — Pixano';
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(Boolean(user));
      setEmail(user?.email || '');
    });

    return unsubscribe;
  }, []);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword('');
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    setSuccess('');

    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion.');
      return;
    }

    setLoading(true);
    try {
      const result = await deleteUserAccount();
      if (result.data?.success) {
        setSuccess('Your account has been deleted.');
        setConfirmText('');
      } else {
        setError('We could not delete the account right now. Please email support.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to delete account. Please email support.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setPassword('');
    setConfirmText('');
    setSuccess('');
    setError('');
  };

  return (
    <div
      className="delete-account-bg"
      style={{
        backgroundImage: 'url(/pix-multilogo-background-white.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="delete-account-container">
        <img src="/pix-logo.png" alt="Pixano Logo" className="delete-account-logo" />
        <div className="delete-account-title">Delete Account</div>
        <div className="delete-account-subtitle">
          Log in here, then confirm delete. If you need help, email support.
        </div>

        {error && <div className="delete-account-error">{error}</div>}
        {success && <div className="delete-account-success">{success}</div>}

        {!isAuthenticated ? (
          <form onSubmit={handleLogin} style={{ width: '100%' }}>
            <input
              type="email"
              className="delete-account-input"
              placeholder="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <input
              type="password"
              className="delete-account-input"
              placeholder="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <button type="submit" className="delete-account-btn" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        ) : (
          <div style={{ width: '100%' }}>
            <div className="delete-account-subtitle">Logged in as {email}</div>
            <div className="delete-account-warning">
              This action is permanent. Type DELETE below and confirm.
            </div>
            <input
              type="text"
              className="delete-account-input"
              placeholder="Type DELETE"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value.toUpperCase())}
            />
            <button
              type="button"
              className="delete-account-danger-btn"
              disabled={loading || confirmText !== 'DELETE'}
              onClick={handleDelete}
            >
              {loading ? 'Deleting...' : 'Confirm Delete Account'}
            </button>
            <button type="button" className="delete-account-btn" disabled={loading} onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        )}

        <div className="delete-account-separator" />
        <div className="delete-account-help">
          Need help? Email{' '}
          <a href="mailto:support@pixano.ai" className="delete-account-link">
            support@pixano.ai
          </a>
        </div>
        <div className="delete-account-disclaimer">
          Deleting your account will permanently remove your personal information, including your email, username, and
          account data.
          <br />
          <br />
          Content you have submitted (such as photos or videos) may be retained by Pixano for dataset development,
          research, and commercial purposes, including the creation and licensing of datasets to third parties.
          <br />
          <br />
          This content will no longer be associated with your identity.
        </div>
      </div>
    </div>
  );
}
